import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button } from '../../components/ui';
import { AccordionItem } from '../../components/ui/Accordion';
import { CheckCircleIcon, ClockIcon, ExternalLinkIcon, XIcon } from '../../components/ui/Icons';
import { Hint } from '../../components/ui/Hint';
import { formatDateTime } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { MgaCloseApproval, MgaPeriod } from './useData';

function approvalUserName(
  user?: {
    userDisplayName?: string | null;
    userFirstName?: string;
    userLastName?: string;
  } | null,
) {
  if (!user) return null;
  const display = user.userDisplayName?.trim();
  if (display) return display;
  return [user.userFirstName, user.userLastName].filter(Boolean).join(' ').trim() || null;
}

const STATUS_LABEL: Record<MgaCloseApproval['status'], string> = {
  DRAFT: 'Returned',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const HISTORY_LABEL: Record<
  NonNullable<MgaCloseApproval['actionHistory']>[number]['action'],
  string
> = {
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RETURNED: 'Returned for changes',
  CANCELLED: 'Cancelled',
};

export function closeApprovalBadgeVariant(
  status: MgaCloseApproval['status'],
): 'default' | 'warning' | 'success' | 'danger' | 'info' | 'secondary' {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
      return 'danger';
    case 'DRAFT':
      return 'warning';
    default:
      return 'secondary';
  }
}

function shouldDefaultOpen(status: MgaCloseApproval['status'] | undefined) {
  return status === 'PENDING' || status === 'DRAFT' || status === 'REJECTED';
}

function statusTone(status: MgaCloseApproval['status'] | 'NONE', isDark: boolean) {
  switch (status) {
    case 'PENDING':
      return {
        bar: isDark ? 'bg-amber-400' : 'bg-amber-500',
        soft: isDark ? 'bg-amber-950/35' : 'bg-amber-50',
        title: isDark ? 'text-amber-100' : 'text-amber-950',
        body: isDark ? 'text-amber-200/85' : 'text-amber-900/80',
        icon: isDark ? 'text-amber-400' : 'text-amber-600',
      };
    case 'DRAFT':
      return {
        bar: isDark ? 'bg-amber-400' : 'bg-amber-500',
        soft: isDark ? 'bg-amber-950/35' : 'bg-amber-50',
        title: isDark ? 'text-amber-100' : 'text-amber-950',
        body: isDark ? 'text-amber-200/85' : 'text-amber-900/80',
        icon: isDark ? 'text-amber-400' : 'text-amber-600',
      };
    case 'APPROVED':
      return {
        bar: isDark ? 'bg-emerald-400' : 'bg-emerald-500',
        soft: isDark ? 'bg-emerald-950/35' : 'bg-emerald-50',
        title: isDark ? 'text-emerald-100' : 'text-emerald-950',
        body: isDark ? 'text-emerald-200/85' : 'text-emerald-900/80',
        icon: isDark ? 'text-emerald-400' : 'text-emerald-600',
      };
    case 'REJECTED':
      return {
        bar: isDark ? 'bg-red-400' : 'bg-red-500',
        soft: isDark ? 'bg-red-950/35' : 'bg-red-50',
        title: isDark ? 'text-red-100' : 'text-red-950',
        body: isDark ? 'text-red-200/85' : 'text-red-900/80',
        icon: isDark ? 'text-red-400' : 'text-red-600',
      };
    default:
      return {
        bar: isDark ? 'bg-slate-500' : 'bg-slate-400',
        soft: isDark ? 'bg-slate-900/50' : 'bg-slate-50',
        title: isDark ? 'text-slate-100' : 'text-slate-900',
        body: isDark ? 'text-slate-300' : 'text-slate-600',
        icon: isDark ? 'text-slate-400' : 'text-slate-500',
      };
  }
}

function historyCardTone(
  action: NonNullable<MgaCloseApproval['actionHistory']>[number]['action'],
  isDark: boolean,
) {
  switch (action) {
    case 'APPROVED':
      return {
        frame: isDark ? 'border-emerald-500' : 'border-emerald-500',
        divider: isDark ? 'border-emerald-500/40' : 'border-emerald-200',
        label: isDark ? 'text-emerald-300' : 'text-emerald-700',
        connector: isDark ? 'bg-emerald-500' : 'bg-emerald-400',
      };
    case 'REJECTED':
      return {
        frame: isDark ? 'border-red-500' : 'border-red-500',
        divider: isDark ? 'border-red-500/40' : 'border-red-200',
        label: isDark ? 'text-red-300' : 'text-red-700',
        connector: isDark ? 'bg-red-500' : 'bg-red-400',
      };
    case 'RETURNED':
      return {
        frame: isDark ? 'border-amber-500' : 'border-amber-500',
        divider: isDark ? 'border-amber-500/40' : 'border-amber-200',
        label: isDark ? 'text-amber-300' : 'text-amber-700',
        connector: isDark ? 'bg-amber-500' : 'bg-amber-400',
      };
    case 'CANCELLED':
      return {
        frame: isDark ? 'border-slate-500' : 'border-slate-400',
        divider: isDark ? 'border-slate-500/40' : 'border-slate-200',
        label: isDark ? 'text-slate-400' : 'text-slate-500',
        connector: isDark ? 'bg-slate-500' : 'bg-slate-300',
      };
    default:
      return {
        frame: isDark ? 'border-sky-500' : 'border-sky-500',
        divider: isDark ? 'border-sky-500/40' : 'border-sky-200',
        label: isDark ? 'text-sky-300' : 'text-sky-700',
        connector: isDark ? 'bg-sky-500' : 'bg-sky-400',
      };
  }
}

function stepTone(status: MgaCloseApproval['steps'][number]['status'], isDark: boolean) {
  switch (status) {
    case 'APPROVED':
      return {
        frame: isDark ? 'border-emerald-500' : 'border-emerald-500',
        divider: isDark ? 'border-emerald-500/40' : 'border-emerald-200',
        label: isDark ? 'text-emerald-300' : 'text-emerald-700',
        connector: isDark ? 'bg-emerald-500' : 'bg-emerald-400',
      };
    case 'REJECTED':
      return {
        frame: isDark ? 'border-red-500' : 'border-red-500',
        divider: isDark ? 'border-red-500/40' : 'border-red-200',
        label: isDark ? 'text-red-300' : 'text-red-700',
        connector: isDark ? 'bg-red-500' : 'bg-red-400',
      };
    case 'SKIPPED':
      return {
        frame: isDark ? 'border-slate-500' : 'border-slate-400',
        divider: isDark ? 'border-slate-500/40' : 'border-slate-200',
        label: isDark ? 'text-slate-400' : 'text-slate-500',
        connector: isDark ? 'bg-slate-500' : 'bg-slate-300',
      };
    default:
      return {
        frame: isDark ? 'border-amber-500' : 'border-amber-500',
        divider: isDark ? 'border-amber-500/40' : 'border-amber-200',
        label: isDark ? 'text-amber-300' : 'text-amber-700',
        connector: isDark ? 'bg-amber-500' : 'bg-amber-400',
      };
  }
}

const MetaCell: React.FC<{ label: string; value: React.ReactNode; hint?: string | null }> = ({
  label,
  value,
  hint,
}) => {
  const { colors } = useTheme();
  return (
    <div className="min-w-0 px-3 py-3 sm:px-4">
      <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>{label}</p>
      <div className={cn('mt-1.5 truncate text-sm font-semibold', colors.text)} title={typeof value === 'string' ? value : undefined}>
        {value}
      </div>
      {hint ? (
        <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)} title={hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};

export function CloseApprovalStatusCard({
  period,
  className,
}: {
  period: Pick<MgaPeriod, 'status' | 'masterApprovedByName' | 'masterApprovedAt' | 'closedAt' | 'closeApproval'>;
  className?: string;
}) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const closeApproval = period.closeApproval ?? null;
  const submittedBy = approvalUserName(closeApproval?.createdByUser);
  const masterName = period.masterApprovedByName || closeApproval?.masterApprovedByName || null;
  const [open, setOpen] = useState(() => shouldDefaultOpen(closeApproval?.status));
  const tone = statusTone(closeApproval?.status ?? 'NONE', isDark);

  useEffect(() => {
    if (shouldDefaultOpen(closeApproval?.status)) setOpen(true);
  }, [closeApproval?.id, closeApproval?.status]);

  const headline = !closeApproval
    ? 'No close request yet'
    : closeApproval.status === 'PENDING'
      ? 'Awaiting office review'
      : closeApproval.status === 'DRAFT'
        ? 'Returned for changes'
        : closeApproval.status === 'APPROVED'
          ? period.status === 'CLOSED'
            ? 'Period closed'
            : 'Close approved'
          : closeApproval.status === 'REJECTED'
            ? 'Close rejected'
            : 'Close cancelled';

  const summary = !closeApproval
    ? 'When the master submits close, the workflow and comments show here.'
    : closeApproval.status === 'PENDING'
      ? 'Shore office is reviewing this period. It locks when approved.'
      : closeApproval.status === 'DRAFT'
        ? 'Update the sheet, then resubmit close from MGA (lock) or Approvals.'
        : closeApproval.status === 'APPROVED'
          ? period.status === 'CLOSED'
            ? 'Payroll and books are locked for this period.'
            : 'Close approval completed.'
          : closeApproval.status === 'REJECTED'
            ? 'Correct the period and submit a new close if needed.'
            : 'This close request was cancelled.';

  const returnComment =
    closeApproval?.status === 'DRAFT' ? closeApproval.lastReturn?.comments?.trim() || null : null;

  const StatusIcon =
    closeApproval?.status === 'APPROVED'
      ? CheckCircleIcon
      : closeApproval?.status === 'REJECTED' || closeApproval?.status === 'CANCELLED'
        ? XIcon
        : ClockIcon;

  const steps = closeApproval?.steps ?? [];
  const history = closeApproval?.actionHistory ?? [];
  /** Oldest → newest so the flow ends on the latest approve / return / submit. */
  const workflowTrail = [...history].sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
  );
  const returnCount = workflowTrail.filter((e) => e.action === 'RETURNED').length;
  const useHistoryWorkflow = workflowTrail.length > 0;

  return (
    <AccordionItem
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span className={cn('text-sm font-semibold', colors.text)}>Close approval</span>
          {closeApproval ? (
            <Badge variant={closeApprovalBadgeVariant(closeApproval.status)}>
              {STATUS_LABEL[closeApproval.status]}
            </Badge>
          ) : (
            <Badge variant="secondary">None</Badge>
          )}
        </span>
      }
      description={
        closeApproval
          ? [closeApproval.title, submittedBy ? `by ${submittedBy}` : null].filter(Boolean).join(' · ')
          : 'Master close request and office review trail'
      }
      open={open}
      onToggle={setOpen}
      headerAction={
        closeApproval ? (
          <Link to={`/approvals/${closeApproval.id}`} onClick={(e) => e.stopPropagation()}>
            <Button type="button" variant="outline" size="sm" className="gap-1.5">
              Open
              <ExternalLinkIcon className="h-3.5 w-3.5 opacity-70" />
            </Button>
          </Link>
        ) : undefined
      }
      className={cn(colors.surface, 'shadow-sm rounded-xl', className)}
    >
      <div className="space-y-5">
        {/* Status hero */}
        <div className={cn('overflow-hidden rounded-xl', tone.soft)}>
          <div className="flex gap-0">
            <div className={cn('w-1 shrink-0', tone.bar)} aria-hidden />
            <div className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3.5 sm:px-5">
              <div
                className={cn(
                  'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                  isDark ? 'bg-black/20' : 'bg-white/70',
                  tone.icon,
                )}
              >
                <StatusIcon className="h-4.5 w-4.5 h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className={cn('text-sm font-semibold', tone.title)}>{headline}</p>
                <p className={cn('mt-1 text-sm leading-relaxed', tone.body)}>{summary}</p>
                {returnComment ? (
                  <blockquote
                    className={cn(
                      'mt-3 border-l-2 pl-3 text-sm italic leading-relaxed',
                      isDark ? 'border-amber-500/60' : 'border-amber-400',
                      tone.body,
                    )}
                  >
                    “{returnComment}”
                  </blockquote>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Meta strip */}
        <div className={cn('overflow-hidden rounded-xl border', colors.border)}>
          <div className={cn('grid divide-x divide-y sm:grid-cols-2 lg:grid-cols-4 lg:divide-y-0', colors.border)}>
            <MetaCell label="Period" value={period.status} hint={period.closedAt ? formatDateTime(period.closedAt) : null} />
            <MetaCell
              label="Master"
              value={masterName || '—'}
              hint={period.masterApprovedAt ? formatDateTime(period.masterApprovedAt) : null}
            />
            <MetaCell
              label="Submitted"
              value={closeApproval ? formatDateTime(closeApproval.createdAt) || '—' : '—'}
              hint={submittedBy ? `by ${submittedBy}` : null}
            />
            <MetaCell
              label="Type"
              value={
                closeApproval?.approvalType?.name ||
                closeApproval?.approvalType?.code ||
                (closeApproval ? 'Period close' : '—')
              }
              hint={closeApproval ? `#${closeApproval.id}` : null}
            />
          </div>
        </div>

        {/* Workflow — full trail of submits / returns / approve (not only current steps) */}
        {useHistoryWorkflow ? (
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
                Workflow
              </p>
              <p className={cn('text-xs', colors.textMuted)}>
                {workflowTrail.length} event{workflowTrail.length === 1 ? '' : 's'}
                {returnCount > 0
                  ? ` · ${returnCount} return${returnCount === 1 ? '' : 's'}`
                  : ''}
              </p>
            </div>
            <div className="overflow-x-auto pb-1">
              <ol className="flex min-w-min items-stretch gap-0">
                {workflowTrail.map((entry, index) => {
                  const who =
                    entry.byUserName ||
                    (entry.byUserId ? `User #${entry.byUserId}` : null) ||
                    'Unknown';
                  const st = historyCardTone(entry.action, isDark);
                  const title =
                    entry.action === 'RETURNED' || entry.action === 'APPROVED' || entry.action === 'REJECTED'
                      ? entry.label || HISTORY_LABEL[entry.action]
                      : HISTORY_LABEL[entry.action];
                  return (
                    <React.Fragment key={`${entry.action}-${entry.at}-${index}`}>
                      {index > 0 ? (
                        <div className="flex w-8 shrink-0 items-center justify-center" aria-hidden>
                          <div className={cn('h-px w-full', st.connector)} />
                        </div>
                      ) : null}
                      <li
                        className={cn(
                          'flex w-[220px] shrink-0 flex-col overflow-hidden rounded-xl border-2',
                          colors.surface,
                          st.frame,
                        )}
                      >
                        <div
                          className={cn(
                            'flex items-start justify-between gap-2 border-b px-3 py-2',
                            st.divider,
                          )}
                        >
                          <p className={cn('min-w-0 truncate text-sm font-semibold', colors.text)}>
                            {title}
                          </p>
                          <span
                            className={cn(
                              'shrink-0 text-[10px] font-semibold uppercase tracking-wide',
                              st.label,
                            )}
                          >
                            {HISTORY_LABEL[entry.action]}
                          </span>
                        </div>
                        <div className={cn('space-y-1 border-b px-3 py-2', st.divider)}>
                          <p className={cn('truncate text-xs', colors.textMuted)} title={who}>
                            {who}
                            {entry.stage != null ? ` · stage ${entry.stage}` : ''}
                          </p>
                          <p className={cn('text-[11px] leading-snug tabular-nums', colors.textMuted)}>
                            {formatDateTime(entry.at) || '—'}
                          </p>
                        </div>
                        <div className="flex-1 px-3 py-2">
                          {entry.comments?.trim() ? (
                            <Hint
                              content={entry.comments.trim()}
                              label="Comment"
                              side="top"
                              maxWidth={360}
                              onlyWhenTruncated
                              triggerClassName="w-full"
                            >
                              <p
                                className={cn(
                                  'line-clamp-4 text-xs italic leading-relaxed',
                                  colors.textSecondary,
                                )}
                              >
                                “{entry.comments.trim()}”
                              </p>
                            </Hint>
                          ) : (
                            <p className={cn('text-[11px]', colors.textMuted)}>—</p>
                          )}
                        </div>
                      </li>
                    </React.Fragment>
                  );
                })}
              </ol>
            </div>
          </div>
        ) : steps.length > 0 ? (
          <div>
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
                Workflow
              </p>
              <p className={cn('text-xs', colors.textMuted)}>
                {steps.filter((s) => s.status === 'APPROVED').length}/{steps.length} done
              </p>
            </div>
            <div className="overflow-x-auto pb-1">
              <ol className="flex min-w-min items-stretch gap-0">
                {steps.map((step, index) => {
                  const assignee =
                    approvalUserName(step.assigneeUser) ||
                    step.assigneeRank?.abrv ||
                    step.assigneeRank?.name ||
                    step.assigneeDepartment?.name ||
                    'Unassigned';
                  const actedBy = approvalUserName(step.actedByUser);
                  const st = stepTone(step.status, isDark);
                  return (
                    <React.Fragment key={step.id}>
                      {index > 0 ? (
                        <div className="flex w-8 shrink-0 items-center justify-center" aria-hidden>
                          <div className={cn('h-px w-full', st.connector)} />
                        </div>
                      ) : null}
                      <li
                        className={cn(
                          'flex w-[200px] shrink-0 flex-col overflow-hidden rounded-xl border-2',
                          colors.surface,
                          st.frame,
                        )}
                      >
                        <div
                          className={cn(
                            'flex items-start justify-between gap-2 border-b px-3 py-2',
                            st.divider,
                          )}
                        >
                          <p className={cn('min-w-0 truncate text-sm font-semibold', colors.text)}>
                            {step.label || `Stage ${step.stage}`}
                          </p>
                          <span
                            className={cn(
                              'shrink-0 text-[10px] font-semibold uppercase tracking-wide',
                              st.label,
                            )}
                          >
                            {step.status.charAt(0) + step.status.slice(1).toLowerCase()}
                          </span>
                        </div>
                        <div className={cn('space-y-1 border-b px-3 py-2', st.divider)}>
                          <p className={cn('truncate text-xs', colors.textMuted)} title={assignee}>
                            {assignee}
                          </p>
                          {(actedBy || step.actedAt) && (
                            <p className={cn('text-[11px] leading-snug', colors.textMuted)}>
                              {[actedBy, step.actedAt ? formatDateTime(step.actedAt) : null]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          )}
                        </div>
                        <div className="flex-1 px-3 py-2">
                          {step.comments?.trim() ? (
                            <Hint
                              content={step.comments.trim()}
                              label="Comment"
                              side="top"
                              maxWidth={360}
                              onlyWhenTruncated
                              triggerClassName="w-full"
                            >
                              <p
                                className={cn(
                                  'line-clamp-3 text-xs italic leading-relaxed',
                                  colors.textSecondary,
                                )}
                              >
                                “{step.comments.trim()}”
                              </p>
                            </Hint>
                          ) : (
                            <p className={cn('text-[11px]', colors.textMuted)}>—</p>
                          )}
                        </div>
                      </li>
                    </React.Fragment>
                  );
                })}
              </ol>
            </div>
          </div>
        ) : closeApproval?.status === 'DRAFT' ? (
          <p className={cn('text-sm', colors.textMuted)}>
            No workflow events yet — resubmit close to continue the trail.
          </p>
        ) : null}
      </div>
    </AccordionItem>
  );
}
